import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CoinIcon } from "@/components/Icons";
import Avatar from "@/components/Avatar";

const PRIZE_CROWNS = ["/assets/crown-maharaja.png", "/assets/crown-royal.png", "/assets/crown-noble.png"];

function useCountdown(endsAt) {
  const [left, setLeft] = useState("");
  useEffect(() => {
    if (!endsAt) return;
    const tick = () => {
      const ms = new Date(endsAt).getTime() - Date.now();
      if (ms <= 0) { setLeft("00 : 00 : 00"); return; }
      const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000), s = Math.floor((ms % 60000) / 1000);
      setLeft(`${String(h).padStart(2, "0")} : ${String(m).padStart(2, "0")} : ${String(s).padStart(2, "0")}`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  return left;
}

export default function Events() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const countdown = useCountdown(data?.event?.ends_at);

  useEffect(() => {
    api.get("/events/active").then(({ data: d }) => setData(d)).catch(() => setData({ event: null }));
  }, []);

  const join = async () => {
    setBusy(true);
    try {
      await api.post(`/events/${data.event.id}/join`);
      toast.success("🎆 You're in! Send event gifts to climb the ranks");
      setData((d) => ({ ...d, joined: true }));
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="min-h-screen max-w-md mx-auto flex items-center justify-center"><div className="w-8 h-8 rounded-full border-2 border-[#E6B84C] border-t-transparent animate-spin" /></div>;
  if (!data.event) return (
    <div className="min-h-screen max-w-md mx-auto px-4 py-6" data-testid="events-page">
      <div className="flex items-center justify-between mb-6">
        <button data-testid="events-back" onClick={() => navigate(-1)} className="text-[#E6B84C]"><ArrowLeft size={20} /></button>
        <h1 className="font-royal text-2xl gradient-gold-text font-bold tracking-wide">EVENTS</h1>
        <span className="w-5" />
      </div>
      <p className="text-zinc-500 text-sm text-center mt-20">No active events right now — check back soon! 🎆</p>
    </div>
  );

  const daysLeft = Math.max(0, Math.ceil((new Date(data.event.ends_at).getTime() - Date.now()) / 86400000));

  return (
    <div className="min-h-screen max-w-md mx-auto px-4 py-6 pb-10 relative overflow-hidden" data-testid="events-page">
      <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-96 h-44 rounded-full bg-gradient-to-r from-[#FF9E4D]/20 via-[#E6B84C]/20 to-[#FF007F]/20 blur-3xl pointer-events-none" />
      <div className="relative flex items-center justify-between mb-5">
        <button data-testid="events-back" onClick={() => navigate(-1)} className="text-[#E6B84C]"><ArrowLeft size={20} /></button>
        <h1 className="font-royal text-2xl gradient-gold-text font-bold tracking-wide">EVENTS</h1>
        <span className="w-5" />
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="relative glass rounded-3xl p-5 text-center border border-[#FF9933]/40 overflow-hidden">
        <span className="absolute inset-x-0 top-0 h-1 rounded-t-3xl" style={{ background: "linear-gradient(90deg,#FF9933 33%,#FFFFFF 33% 66%,#138808 66%)" }} />
        <span className="absolute top-2 left-4 text-lg">🇮🇳</span>
        <span className="absolute top-3 right-5 text-lg">🎆</span>
        <span className="absolute bottom-3 left-7 text-sm">✨</span>
        <h2 className="font-royal text-2xl font-bold bg-gradient-to-r from-[#FF9933] via-[#FDE08B] to-[#3BB143] bg-clip-text text-transparent" data-testid="event-name">{data.event.name}</h2>
        <p className="text-zinc-400 text-[11px] mt-0.5">{data.event.subtitle} · {daysLeft} day{daysLeft === 1 ? "" : "s"} left</p>
        <p className="font-display text-3xl font-extrabold text-white mt-3 tracking-widest drop-shadow-[0_0_14px_rgba(138,43,226,0.8)]" data-testid="event-countdown">{countdown}</p>
      </motion.div>

      <h2 className="font-royal text-lg gradient-gold-text font-bold mt-6 mb-3">Prize Pool</h2>
      <div className="grid grid-cols-3 gap-2" data-testid="prize-pool">
        {data.event.prizes.map((p, i) => (
          <div key={p.rank} className={`glass rounded-2xl p-3 text-center ${i === 0 ? "border border-[#E6B84C]/50 shadow-[0_0_16px_rgba(230,184,76,0.25)]" : ""}`}>
            <img src={PRIZE_CROWNS[i]} alt="" className="w-10 h-10 object-contain mx-auto" />
            <p className="text-[10px] text-zinc-400 font-bold mt-1">{p.rank}</p>
            <p className="text-white text-xs font-extrabold">₹{p.amount.toLocaleString("en-IN")}</p>
            {p.bonus && <p className="text-[8px] text-[#E6B84C] font-bold mt-0.5">+ {p.bonus}</p>}
          </div>
        ))}
      </div>

      <h2 className="font-royal text-lg gradient-gold-text font-bold mt-6 mb-3">Your Rank</h2>
      <div className="glass rounded-2xl px-4 py-3 flex items-center gap-3" data-testid="event-my-rank">
        <Avatar src={user?.avatar} size={40} tier={user?.tier} />
        <div className="flex-1">
          <p className="text-white text-sm font-bold">{data.my_rank ? `#${data.my_rank}` : "Unranked"} <span className="text-zinc-500 text-[10px] font-semibold">of {data.participants} players</span></p>
          <p className="text-[#E6B84C] text-[11px] font-bold flex items-center gap-1"><CoinIcon size={11} /> {data.my_points.toLocaleString("en-IN")} points</p>
        </div>
        {data.joined && <span className="text-emerald-400 text-[10px] font-bold">Joined ✓</span>}
      </div>

      <h2 className="font-royal text-lg gradient-gold-text font-bold mt-6 mb-3">Event Gifts</h2>
      <div className="grid grid-cols-3 gap-2" data-testid="event-gifts">
        {data.gifts.map((g) => (
          <div key={g.id} className="glass rounded-2xl p-3 text-center">
            {g.icon_url ? <img src={g.icon_url} alt={g.name} className="w-12 h-12 object-contain mx-auto" /> : <p className="text-3xl">{g.emoji}</p>}
            <p className="text-white text-[10px] font-bold mt-1 truncate">{g.name}</p>
            <p className="text-[#E6B84C] text-[10px] font-extrabold flex items-center justify-center gap-0.5"><CoinIcon size={10} /> {g.price.toLocaleString("en-IN")}</p>
          </div>
        ))}
      </div>

      <button data-testid="join-tournament-button" onClick={data.joined ? () => navigate("/") : join} disabled={busy}
        className="mt-7 w-full bg-gradient-to-r from-[#FDE08B] via-[#FF9E4D] to-[#FF007F] text-white font-extrabold font-display py-4 rounded-full shadow-[0_0_24px_rgba(255,0,127,0.45)] active:scale-[0.98] transition-transform disabled:opacity-50 tracking-wide">
        {busy ? "JOINING…" : data.joined ? "GO GIFT & CLIMB RANKS 🚀" : "JOIN TOURNAMENT"}
      </button>
    </div>
  );
}
